import React, { useCallback, useEffect, useState } from 'react';
import { forkJoin, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ClassifierValue from '../../../interfaces/classifierValue';
import Classifier from '../../../enums/classifier';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';
import FieldTypes from '../../../enums/field-types';
import { useService } from '../../../services/services';
import Accordion, {
  AccordionItem,
  AccordionItemBody,
  AccordionItemHeader,
} from '../../../components/common/accordion';
import DownloadLinks from './components/links';
import css from './list.module.css';

interface Model {
  name: string;
  iconId?: string;
  icon?: any;
  items: Array<ModelItem>;
  hasPropertyValueFiles?: boolean;
}

export interface ModelItem {
  id: string;
  name: string;
  fileId?: string;
  fileName: string;
}

export default function DownloadsList() {
  const { farmId, type } = useParams<{ farmId: string; type: Classifier }>();
  const { t } = useTranslation();
  const { financeModelingService, fileService } = useService();
  const [models, setModels] = useState([]);

  useEffect(() => {
    const subscription = forkJoin([
      financeModelingService.warehouseClassifiers(farmId, type),
      financeModelingService.financeClassifiers(farmId, type),
    ]).subscribe(
      ([sectorClassifiers, modelingClassifiers]: [
        ClassifierValue[],
        ClassifierValue[]
      ]) => {
        const groups: { [key: string]: Model } = {};
        let classifierSectorPropertyId: string = null;
        let classifierFilePropertyId: string = null;
        let sectorFilePropertyId: string = null;

        // get sector property ids
        sectorClassifiers.forEach((classifier: ClassifierValue) => {
          Object.keys(classifier.properties).forEach((id) => {
            const { key } = classifier.properties[id].property;

            if (key === FieldTypes.FILE) {
              return (sectorFilePropertyId = id);
            }
          });
        });

        // get classifier property ids
        modelingClassifiers.forEach((classifier: ClassifierValue) => {
          Object.keys(classifier.properties).forEach((id) => {
            const { key } = classifier.properties[id]?.property;

            switch (key) {
              case ClassifierPropertyKey.CROP_GROUP:
              case ClassifierPropertyKey.LIVESTOCK_SECTOR:
                classifierSectorPropertyId = classifierSectorPropertyId || id;
                break;
              case FieldTypes.FILE:
                classifierFilePropertyId = classifierFilePropertyId || id;
                break;
            }
          });
        });

        // build group map
        modelingClassifiers.forEach((classifier: ClassifierValue) => {
          const sectorId =
            classifier.properties[classifierSectorPropertyId].value[0].id;
          const fileId = classifier.properties[classifierFilePropertyId]?.value
            ?.length
            ? classifier.properties[classifierFilePropertyId].value[0].id
            : null;

          if (!groups[sectorId]) {
            groups[sectorId] = {
              name: classifier.properties[classifierSectorPropertyId].value[0]
                .label,
              items: [],
            };

            if (sectorFilePropertyId) {
              const sector = sectorClassifiers.filter(
                (sector: ClassifierValue) => sector.id === sectorId
              )[0];

              const iconId = sector.properties[sectorFilePropertyId]?.value
                ?.length
                ? sector.properties[sectorFilePropertyId].value[0].id
                : null;

              groups[sectorId].iconId = iconId;
              groups[sectorId].icon = (
                <img src="" alt="" id={`icon-${iconId}`} />
              );
            }
          }

          if (fileId) {
            groups[sectorId].hasPropertyValueFiles = true;
          }

          groups[sectorId].items.push({
            id: classifier.id,
            name: classifier.value,
            fileId,
            fileName: fileId
              ? classifier.properties[classifierFilePropertyId].value[0].name
              : null,
          });
        });

        setModels(
          Object.values(groups).sort((a, b) => (a.name > b.name ? 1 : -1))
        );
      }
    );

    return () => subscription.unsubscribe();
  }, [financeModelingService, farmId, type]);

  const downloadIcon = useCallback(
    (id: string) => {
      return fileService.download(id).pipe(
        tap((icon: any) => {
          const src = URL.createObjectURL(new Blob([icon]));
          const imageRef = document.querySelector(`#icon-${id}`);
          imageRef && imageRef.setAttribute('src', src);
        })
      );
    },
    [fileService]
  );

  useEffect(() => {
    if (!models.length) return;

    const IconLoadingSubscriptions: Array<Observable<Blob>> = [];
    models
      .filter((model) => model.iconId)
      .forEach(({ iconId }) => {
        IconLoadingSubscriptions.push(downloadIcon(iconId));
      });

    const subscription = forkJoin(IconLoadingSubscriptions).subscribe();

    return () => subscription.unsubscribe();
  }, [financeModelingService, models, downloadIcon]);

  return (
    <div className="production-modeling-list">
      <Accordion>
        {models &&
          models.map((item: Model, index: number) => {
            return item.hasPropertyValueFiles ? (
              <AccordionItem
                header={
                  <AccordionItemHeader>
                    <h2 className={css['accordion-header']}>
                      {item.iconId ? (
                        <span className={css['accordion-icon']}>
                          {item.icon}
                        </span>
                      ) : (
                        ''
                      )}
                      {item.name}
                    </h2>
                  </AccordionItemHeader>
                }
                body={
                  <AccordionItemBody>
                    <DownloadLinks items={item.items} />
                  </AccordionItemBody>
                }
                opened={index === 0}
                key={`accordion-item-${index}`}
              />
            ) : null;
          })}
      </Accordion>

      {models.length &&
      !models.filter((item: Model) => item.hasPropertyValueFiles).length ? (
        <p>{t('pages.finance.modeling.not_found')}</p>
      ) : (
        ''
      )}
    </div>
  );
}
